/**
 * Video calls store
 *
 * The video state of each of my softphone calls, keyed by call id: whether my camera is on, whether the other party's
 * video is arriving, the streams to show, and what went wrong. SipVideo writes it; the video view and the camera buttons
 * read it.
 */

import { markRaw, ref } from 'vue'
import { defineStore } from 'pinia'
import type { PbxPeerVideo } from '@/TcxApi/PbxApp'

/** My camera on the call */
export type LocalCamera = 'off' | 'starting' | 'on' | 'error'

/** The other party's video: none received, received but no frames right now (e.g. a spy joined the bridge), or live */
export type RemoteCamera = 'none' | 'paused' | 'live'

/** The class of the other party's device - published by the PBX only when both ends are video-enabled */
export type PeerVideo = PbxPeerVideo

export interface CallVideoState {
	callId: string
	peerVideo?: PeerVideo
	local: LocalCamera
	remote: RemoteCamera
	localStream?: MediaStream
	remoteStream?: MediaStream
	remoteAspect?: number // videoWidth / videoHeight of the remote video
	unavailable?: boolean // set after a rejected upgrade: the camera button stays, disabled, for the rest of the call
	error?: 'permission' | 'busy' | 'notfound' | 'rejected' | 'media'
	resumeCamera?: boolean // set while on hold when my camera was on: it comes back on at unhold
}

/** The fields of a call's video state that can be changed */
export type CallVideoPatch = Partial<Omit<CallVideoState, 'callId'>>

/**
 * Whether my camera is on a call, or on its way
 * @param video The call's video state
 */
export function cameraInUse(video: CallVideoState | undefined): boolean {
	return video?.local === 'on' || video?.local === 'starting'
}

export const useVideoCallsStore = defineStore('videoCalls', () => {
	/** Video state per call id */
	const calls = ref<Record<string, CallVideoState>>({})

	/**
	 * Get a call's video state, creating it (camera off, no remote video) if it doesn't exist yet
	 * @param callId The call id
	 * @returns The call's video state
	 */
	function ensure(callId: string): CallVideoState {
		calls.value[callId] ??= { callId, local: 'off', remote: 'none' }
		return calls.value[callId]
	}

	/**
	 * Change some of a call's video state, creating it first if needed
	 * @param callId The call id
	 * @param partial The fields to change - a field given as undefined is cleared
	 */
	function patch(callId: string, partial: CallVideoPatch) {
		// Streams are browser objects: keep them out of Vue's reactivity, the <video> elements need the originals
		if (partial.localStream) { markRaw(partial.localStream) }
		if (partial.remoteStream) { markRaw(partial.remoteStream) }

		Object.assign(ensure(callId), partial)
	}

	/**
	 * Forget a call's video state, when the call has ended
	 * @param callId The call id
	 */
	function remove(callId: string) {
		delete calls.value[callId] // eslint-disable-line @typescript-eslint/no-dynamic-delete
	}

	return { calls, ensure, patch, remove }
})
